import React, { useEffect, useMemo, useState } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { initialTokenList } from '../constant/constant';
import {
    convertBigToSmallDigitWithSpecificDecimal,
    getTokenBalance,
    shortenWalletAddress,
} from '../utils/commonFunction';

function pickUsdcToken() {
    if (!Array.isArray(initialTokenList)) return null;
    return (
        initialTokenList.find((t) => String(t?.symbol || '').toUpperCase() === 'USDC') ||
        initialTokenList[0] ||
        null
    );
}

function useWalletUsdcBalance(walletAddress) {
    const token = useMemo(() => pickUsdcToken(), []);
    const decimals = Number(token?.decimals) || 6;
    const tokenAddress = token?.address;
    const [formatted, setFormatted] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!walletAddress || !tokenAddress) {
            setFormatted(null);
            return undefined;
        }

        const load = async () => {
            try {
                const raw = await getTokenBalance(tokenAddress, walletAddress);
                if (!cancelled) {
                    setFormatted(convertBigToSmallDigitWithSpecificDecimal(raw, decimals));
                }
            } catch {
                if (!cancelled) setFormatted('—');
            }
        };

        setFormatted(null);
        load();
        const id = setInterval(load, 60_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [walletAddress, tokenAddress, decimals]);

    return {
        formatted,
        symbol: token?.symbol || 'USDC',
    };
}

const chainButtonClass =
    'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-zinc-200 bg-zinc-50 px-2.5 text-xs font-semibold text-zinc-800 transition hover:border-zinc-300 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/80';

const accountButtonClass =
    'inline-flex h-10 max-w-full min-w-0 items-center gap-2 rounded-xl border border-zinc-200 bg-white px-2.5 py-1.5 text-left transition hover:border-teal-300 hover:bg-teal-50/40 dark:border-zinc-700 dark:bg-zinc-950 dark:hover:border-teal-700 dark:hover:bg-teal-950/30';

const connectButtonClass =
    'inline-flex h-10 items-center justify-center rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-500 dark:bg-teal-600 dark:hover:bg-teal-500';

const compactConnectClass =
    'inline-flex h-10 shrink-0 items-center justify-center rounded-xl bg-teal-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-500 dark:bg-teal-600 dark:hover:bg-teal-500';

const compactAccountClass =
    'inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-zinc-200 bg-white transition hover:border-teal-300 hover:bg-teal-50/40 dark:border-zinc-700 dark:bg-zinc-950 dark:hover:border-teal-700 dark:hover:bg-teal-950/30';

function AccountJazzicon({ address, diameter }) {
    if (!address || typeof address !== 'string') return null;
    try {
        return <Jazzicon diameter={diameter} seed={jsNumberForAddress(address)} />;
    } catch {
        return null;
    }
}

/** Renders chain switch + jazzicon, address, and USDC balance (hooks-safe). */
function ConnectedWalletBar({ account, chain, openAccountModal, openChainModal, isMobile }) {
    const jazzDiameter = isMobile ? 32 : 28;
    const { formatted: usdcFormatted, symbol: usdcSymbol } = useWalletUsdcBalance(account.address);

    return (
        <div
            className={`flex min-w-0 items-center gap-2 ${isMobile ? 'w-full flex-col' : 'flex-row flex-wrap justify-end'}`}
        >
            <button type="button" onClick={openChainModal} className={chainButtonClass}>
                {chain.hasIcon && (
                    <span
                        className="h-5 w-5 overflow-hidden rounded-full"
                        style={{ background: chain.iconBackground }}
                    >
                        {chain.iconUrl ? (
                            <img
                                alt={chain.name || ''}
                                src={chain.iconUrl}
                                className="h-full w-full object-cover"
                            />
                        ) : null}
                    </span>
                )}
                <span className="max-w-[120px] truncate">{chain.name}</span>
            </button>

            <button
                type="button"
                onClick={openAccountModal}
                className={`${accountButtonClass} ${isMobile ? 'w-full' : ''}`}
            >
                <span
                    className="shrink-0 overflow-hidden rounded-full"
                    style={{ width: jazzDiameter, height: jazzDiameter }}
                >
                    <AccountJazzicon address={account.address} diameter={jazzDiameter} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate font-mono text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                        {shortenWalletAddress(account.address)}
                    </span>
                    <span className="truncate text-[11px] font-medium text-teal-700 dark:text-teal-400">
                        {usdcFormatted === null ? (
                            <span className="text-zinc-400 dark:text-zinc-500">Balance…</span>
                        ) : (
                            <>
                                {usdcFormatted} {usdcSymbol}
                            </>
                        )}
                    </span>
                </span>
            </button>
        </div>
    );
}

function CompactWalletControls() {
    return (
        <ConnectButton.Custom>
            {({
                account,
                chain,
                openAccountModal,
                openChainModal,
                openConnectModal,
                authenticationStatus,
                mounted,
            }) => {
                const ready = mounted && authenticationStatus !== 'loading';
                const connected =
                    ready &&
                    account &&
                    chain &&
                    (!authenticationStatus || authenticationStatus === 'authenticated');

                return (
                    <div
                        {...(!ready && {
                            'aria-hidden': true,
                            style: {
                                opacity: 0,
                                pointerEvents: 'none',
                                userSelect: 'none',
                            },
                        })}
                    >
                        {(() => {
                            if (!connected) {
                                return (
                                    <button
                                        type="button"
                                        onClick={openConnectModal}
                                        className={compactConnectClass}
                                    >
                                        Connect
                                    </button>
                                );
                            }
                            if (chain.unsupported) {
                                return (
                                    <button
                                        type="button"
                                        onClick={openChainModal}
                                        className={compactConnectClass}
                                        title="Wrong network"
                                    >
                                        <i className="fa-solid fa-triangle-exclamation text-sm" aria-hidden />
                                        <span className="sr-only">Wrong network</span>
                                    </button>
                                );
                            }

                            return (
                                <button
                                    type="button"
                                    onClick={openAccountModal}
                                    className={compactAccountClass}
                                    title={shortenWalletAddress(account.address)}
                                >
                                    <AccountJazzicon address={account.address} diameter={32} />
                                </button>
                            );
                        })()}
                    </div>
                );
            }}
        </ConnectButton.Custom>
    );
}

function WalletControls({ isMobile = false }) {
    return (
        <ConnectButton.Custom>
            {({
                account,
                chain,
                openAccountModal,
                openChainModal,
                openConnectModal,
                authenticationStatus,
                mounted,
            }) => {
                const ready = mounted && authenticationStatus !== 'loading';
                const connected =
                    ready &&
                    account &&
                    chain &&
                    (!authenticationStatus || authenticationStatus === 'authenticated');

                return (
                    <div
                        {...(!ready && {
                            'aria-hidden': true,
                            style: {
                                opacity: 0,
                                pointerEvents: 'none',
                                userSelect: 'none',
                            },
                        })}
                    >
                        {(() => {
                            if (!connected) {
                                return (
                                    <button type="button" onClick={openConnectModal} className={connectButtonClass}>
                                        Connect Wallet
                                    </button>
                                );
                            }
                            if (chain.unsupported) {
                                return (
                                    <button type="button" onClick={openChainModal} className={connectButtonClass}>
                                        Wrong network
                                    </button>
                                );
                            }

                            return (
                                <ConnectedWalletBar
                                    account={account}
                                    chain={chain}
                                    openAccountModal={openAccountModal}
                                    openChainModal={openChainModal}
                                    isMobile={isMobile}
                                />
                            );
                        })()}
                    </div>
                );
            }}
        </ConnectButton.Custom>
    );
}

const Profile = ({ isMobile = false, compact = false }) => {
    if (isMobile) {
        return (
            <div className="w-full">
                <div className="rounded-3xl border border-zinc-200 bg-zinc-50/90 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                    <WalletControls isMobile />
                </div>
            </div>
        );
    }

    if (compact) {
        return <CompactWalletControls />;
    }

    return (
        <div className="max-w-full overflow-hidden">
            <WalletControls isMobile={false} />
        </div>
    );
};

export default Profile;
